"use client";

import { useRef } from "react";
import type { MouseEvent } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeftIcon, ArrowRightIcon } from "@radix-ui/react-icons";

export type RomaMenuCarouselItem = {
  name: string;
  displayName: string;
  sectionTitle: string;
  price: string;
  description?: string;
  photoUrl: string;
};

type RomaMenuCarouselProps = {
  items: readonly RomaMenuCarouselItem[];
  menuHref: string;
  /** Grubhub ordering link. Kept in the API for a future card-level order
   *  affordance, but the per-card "+" shortcut is disabled for now. */
  orderHref?: string;
};

function isExternalHref(href: string): boolean {
  return /^https?:\/\//.test(href);
}

export function RomaMenuCarousel({ items, menuHref }: RomaMenuCarouselProps) {
  const scrollerRef = useRef<HTMLOListElement>(null);
  const linkProps = isExternalHref(menuHref)
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};

  function scrollByCard(
    event: MouseEvent<HTMLAnchorElement>,
    direction: "previous" | "next",
  ) {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    event.preventDefault();
    const cards = Array.from(scroller.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );
    if (cards.length === 0) return;

    const currentLeft = scroller.scrollLeft;
    const currentIndex = cards.reduce((closestIndex, card, index) => {
      const closestCard = cards[closestIndex];
      return Math.abs(card.offsetLeft - currentLeft) <
        Math.abs(closestCard.offsetLeft - currentLeft)
        ? index
        : closestIndex;
    }, 0);
    const targetIndex =
      direction === "next"
        ? Math.min(cards.length - 1, currentIndex + 1)
        : Math.max(0, currentIndex - 1);
    const targetCard = cards[targetIndex];

    scroller.scrollTo({
      left: targetCard.offsetLeft,
      behavior: "smooth",
    });
  }

  return (
    <div className="mt-10 sm:mt-14" data-funnel-priority="roma-menu-carousel">
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--roma-espresso)]/52">
          Swipe the plates
        </p>
        <div className="flex items-center gap-2">
          <a
            href="#roma-menu-card-1"
            aria-label="Previous plate"
            onClick={(event) => scrollByCard(event, "previous")}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[var(--roma-parchment-line)] bg-transparent text-[var(--roma-terracotta)] transition-colors hover:bg-[var(--roma-linen)]/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--roma-terracotta)]"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          </a>
          <a
            href="#roma-menu-card-2"
            aria-label="Next plate"
            onClick={(event) => scrollByCard(event, "next")}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[var(--roma-parchment-line)] bg-transparent text-[var(--roma-terracotta)] transition-colors hover:bg-[var(--roma-linen)]/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--roma-terracotta)]"
          >
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>

      <ol
        ref={scrollerRef}
        aria-label="Most requested Roma plates"
        className="-mx-4 grid auto-cols-[minmax(17.5rem,82vw)] grid-flow-col gap-3 overflow-x-auto scroll-smooth px-4 pb-2 snap-x snap-mandatory [scrollbar-width:none] sm:mx-0 sm:auto-cols-[minmax(22rem,42vw)] sm:px-0 lg:auto-cols-[minmax(24rem,31rem)] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item, index) => (
          <li
            id={`roma-menu-card-${index + 1}`}
            key={item.name}
            className="group relative flex snap-start flex-col overflow-hidden rounded-[var(--roma-radius)] border border-[var(--roma-frame)] bg-[var(--roma-ivory)] shadow-[0_26px_70px_-50px_rgba(15,14,14,0.45)]"
          >
            <div className="relative aspect-[4/3] overflow-hidden bg-[var(--roma-linen)]">
              <Link
                href={menuHref}
                prefetch={false}
                {...linkProps}
                data-track="menu_clicked"
                data-track-location="home_roma_menu_carousel"
                data-track-label={item.displayName}
                className="block h-full w-full"
                aria-label={`See ${item.displayName} on the Roma menu`}
              >
                <Image
                  src={item.photoUrl}
                  alt={item.displayName}
                  fill
                  sizes="(max-width: 640px) 82vw, (max-width: 1024px) 42vw, 31rem"
                  quality={72}
                  className="object-cover object-center transition-transform duration-700 group-hover:scale-[1.04]"
                />
              </Link>
            </div>
            <Link
              href={menuHref}
              prefetch={false}
              {...linkProps}
              data-track="menu_clicked"
              data-track-location="home_roma_menu_carousel"
              data-track-label={item.displayName}
              className="flex min-h-[12rem] flex-1 flex-col gap-3 px-5 py-6 sm:px-6 sm:py-7"
            >
              <p className="roma-eyebrow text-[11px] tracking-[0.2em]">
                {String(index + 1).padStart(2, "0")} · {item.sectionTitle}
              </p>
              <div className="flex items-baseline justify-between gap-4">
                <h3 className="font-serif text-2xl font-normal italic leading-tight text-[var(--roma-espresso)] sm:text-[1.65rem]">
                  {item.displayName}
                </h3>
                <span className="menu-price font-serif text-sm italic sm:text-base">
                  {item.price}
                </span>
              </div>
              {item.description ? (
                <p className="text-sm leading-relaxed text-[var(--roma-espresso)]/65">
                  {item.description}
                </p>
              ) : null}
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default RomaMenuCarousel;
